import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { CatalogIndex } from "@itaas/pep-contracts";
import { getProducts } from "../api/pep-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { DomainIcon, type DomainIconName } from "../home/HomeIcons";
import { usePepFetch } from "../hooks/usePepFetch";
import { usePepI18n } from "../i18n/usePepI18n";
import {
  PRODUCTS_HERO_PANELS,
  PRODUCTS_LIMITED,
  PRODUCTS_PRIMARY,
  PRODUCTS_PUBLISHABLE_SLUGS,
  productsCopy,
  type ProductsEditorial,
  type ProductsSlug,
} from "../products/products-content";

type CatalogProduct = CatalogIndex["products"][number];

const TONE_ICON: Record<ProductsSlug, DomainIconName> = {
  edu: "education",
  rh: "hr",
  mineportal: "mines",
  inventory: "ops",
  erp: "erp",
  billing: "finance",
  hospitol: "health",
  security: "security",
};

function editorialName(editorial: ProductsEditorial, en: boolean) {
  return en ? editorial.commercialNameEn : editorial.commercialNameFr;
}

function matchesSearch(editorial: ProductsEditorial, q: string, en: boolean): boolean {
  if (!q) return true;
  const hay = [
    editorialName(editorial, en),
    en ? editorial.promiseEn : editorial.promiseFr,
    ...(en ? editorial.capacitiesEn : editorial.capacitiesFr),
    editorial.slug,
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

function TrustIcon({ id }: { id: string }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    "aria-hidden": true as const,
  };
  if (id === "rdc") {
    return (
      <svg {...common}>
        <path d="M12 21s7-4.4 7-11a7 7 0 1 0-14 0c0 6.6 7 11 7 11Z" />
        <circle cx="12" cy="10" r="2.2" />
      </svg>
    );
  }
  if (id === "support") {
    return (
      <svg {...common}>
        <path d="M12 3a7 7 0 0 0-7 7v2.2a2.8 2.8 0 0 0 2.8 2.8h.4V12H7.5V10a4.5 4.5 0 0 1 9 0v2h-1.7v3h.4A2.8 2.8 0 0 0 19 12.2V10a7 7 0 0 0-7-7Z" />
        <path d="M9.5 18.5h5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M12 3 19 6.5v5.2c0 4.2-2.8 7.6-7 8.8-4.2-1.2-7-4.6-7-8.8V6.5L12 3Z" />
      <path d="M9.5 12.2 11.2 14l3.5-4" />
    </svg>
  );
}

function PrimaryCard({
  editorial,
  locale,
  discoverLabel,
}: {
  editorial: ProductsEditorial;
  locale: "fr" | "en";
  discoverLabel: string;
}) {
  const en = locale === "en";
  const capacities = en ? editorial.capacitiesEn : editorial.capacitiesFr;
  return (
    <article className={`pep-prod-card pep-prod-card--${editorial.tone}`} data-product={editorial.slug}>
      <div className="pep-prod-card__top">
        <span className="pep-prod-card__icon" aria-hidden="true">
          <DomainIcon name={TONE_ICON[editorial.slug]} />
        </span>
        <h3 className="pep-prod-card__name">{editorialName(editorial, en)}</h3>
      </div>
      <div className="pep-prod-card__media">
        <img
          src={editorial.imageSrc}
          alt={en ? editorial.imageAltEn : editorial.imageAltFr}
          width={480}
          height={300}
          loading="lazy"
          decoding="async"
        />
      </div>
      <p className="pep-prod-card__promise">{en ? editorial.promiseEn : editorial.promiseFr}</p>
      <ul className="pep-prod-card__list">
        {capacities.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <Link className="pep-prod-card__cta" to={`/products/${editorial.slug}`}>
        {discoverLabel} →
      </Link>
    </article>
  );
}

function LimitedCard({
  editorial,
  locale,
  badge,
  discoverLabel,
}: {
  editorial: ProductsEditorial;
  locale: "fr" | "en";
  badge: string;
  discoverLabel: string;
}) {
  const en = locale === "en";
  const capacities = en ? editorial.capacitiesEn : editorial.capacitiesFr;
  return (
    <article className={`pep-prod-limited pep-prod-limited--${editorial.tone}`} data-product={editorial.slug}>
      <div className="pep-prod-limited__copy">
        <div className="pep-prod-limited__head">
          <span className="pep-prod-limited__icon" aria-hidden="true">
            <DomainIcon name={TONE_ICON[editorial.slug]} />
          </span>
          <span className="pep-prod-badge">{badge}</span>
        </div>
        <h3 className="pep-prod-limited__name">{editorialName(editorial, en)}</h3>
        <p className="pep-prod-limited__promise">{en ? editorial.promiseEn : editorial.promiseFr}</p>
        <ul className="pep-prod-limited__list">
          {capacities.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <Link className="pep-prod-limited__cta" to={`/products/${editorial.slug}`}>
          {discoverLabel} →
        </Link>
      </div>
      <div className="pep-prod-limited__media">
        <img
          src={editorial.imageSrc}
          alt={en ? editorial.imageAltEn : editorial.imageAltFr}
          width={420}
          height={280}
          loading="lazy"
          decoding="async"
        />
      </div>
    </article>
  );
}

export function ProductsPage() {
  const { locale } = usePepI18n();
  const copy = productsCopy(locale);
  const en = locale === "en";
  const { data: products, loading, error, reload } = usePepFetch(getProducts);
  const [search, setSearch] = useState("");

  const bySlug = useMemo(() => {
    const map = new Map<string, CatalogProduct>();
    for (const p of products ?? []) {
      if (PRODUCTS_PUBLISHABLE_SLUGS.has(p.slug)) map.set(p.slug, p);
    }
    return map;
  }, [products]);

  /** Prefer API presence; fall back to editorial so UI still narrates portfolio if API lags. */
  const primary = useMemo(() => {
    const q = search.trim().toLowerCase();
    return PRODUCTS_PRIMARY.filter((e) => {
      if (products && !bySlug.has(e.slug)) return false;
      return matchesSearch(e, q, en);
    });
  }, [bySlug, products, search, en]);

  const limited = useMemo(() => {
    const q = search.trim().toLowerCase();
    return PRODUCTS_LIMITED.filter((e) => {
      if (products && !bySlug.has(e.slug)) return false;
      return matchesSearch(e, q, en);
    });
  }, [bySlug, products, search, en]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://www.itaas-suite.com/products#webpage",
        name: copy.seoTitle,
        description: copy.seoDescription,
        url: "https://www.itaas-suite.com/products",
        inLanguage: en ? "en" : "fr",
        isPartOf: { "@type": "WebSite", name: "ITaaS Suite", url: "https://www.itaas-suite.com/" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "ITaaS Suite",
            item: "https://www.itaas-suite.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: en ? "Products" : "Produits",
            item: "https://www.itaas-suite.com/products",
          },
        ],
      },
      {
        "@type": "ItemList",
        name: copy.portfolioTitle,
        itemListElement: PRODUCTS_PRIMARY.map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: editorialName(p, en),
          url: `https://www.itaas-suite.com/products/${p.slug}`,
        })),
      },
    ],
  };

  return (
    <SiteLayout>
      <SeoHead
        title={copy.seoTitle}
        description={copy.seoDescription}
        keywords={[...copy.seoKeywords]}
        canonicalPath="/products"
        locale={locale}
        jsonLd={jsonLd}
      />

      <section className="pep-prod-hero" data-section="P-HERO" aria-labelledby="products-h1">
        <div className="container pep-prod-hero__grid">
          <div className="pep-prod-hero__copy">
            <p className="pep-prod-eyebrow">{copy.eyebrow}</p>
            <h1 id="products-h1" className="pep-prod-hero__title">
              {copy.h1Before}
              <span className="pep-prod-hero__emphasis">{copy.h1Emphasis}</span>
              {copy.h1After}
            </h1>
            <p className="pep-prod-hero__lead">{copy.lead}</p>

            <label className="pep-prod-search">
              <span className="visually-hidden">{copy.searchAria}</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.7" />
                <path d="M16.2 16.2 20 20" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={copy.searchPlaceholder}
                aria-label={copy.searchAria}
              />
            </label>

            <ul className="pep-prod-trust list-unstyled">
              {copy.trust.map((t) => (
                <li key={t.id}>
                  <TrustIcon id={t.id} />
                  <div>
                    <strong>{t.title}</strong>
                    <span>{t.body}</span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="pep-prod-hero__actions">
              <Link className="btn pep-prod-btn-primary" to={copy.compareHref}>
                {copy.compareCta}
              </Link>
              <Link className="btn pep-prod-btn-secondary" to={copy.solutionsHref}>
                {copy.solutionsCta} →
              </Link>
            </div>
          </div>

          <div className="pep-prod-hero__visual" aria-label={en ? "Business domains" : "Univers métiers"}>
            <div className="pep-prod-mosaic" data-mosaic="products-hero">
              {PRODUCTS_HERO_PANELS.map((panel, i) => (
                <figure
                  key={panel.id}
                  className={`pep-prod-mosaic__cell pep-prod-mosaic__cell--${panel.id}`}
                  data-domain={panel.id}
                >
                  <img
                    src={panel.imageSrc}
                    alt={en ? panel.altEn : panel.altFr}
                    width={640}
                    height={480}
                    decoding="async"
                    fetchPriority={i < 2 ? "high" : "low"}
                    sizes="(max-width: 767px) 50vw, (max-width: 1100px) 40vw, 28vw"
                  />
                  <figcaption>{en ? panel.labelEn : panel.labelFr}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className="pep-prod-portfolio"
        id="products-portfolio"
        data-section="P-PORTFOLIO"
        aria-labelledby="products-portfolio-h2"
      >
        <div className="container">
          {error ? (
            <div className="pep-prod-api-error" role="alert">
              <strong>{copy.apiErrorTitle}</strong>
              <p>{copy.apiErrorBody}</p>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => void reload()}>
                {copy.retry}
              </button>
            </div>
          ) : null}
          {loading && !products && !error ? <p className="text-secondary">{copy.loading}</p> : null}

          <div className="pep-prod-section-head">
            <div>
              <p className="pep-prod-eyebrow">{copy.portfolioEyebrow}</p>
              <h2 id="products-portfolio-h2" className="pep-prod-section-title">
                {copy.portfolioTitle}
              </h2>
              <p className="pep-prod-section-lead">{copy.portfolioLead}</p>
            </div>
            <a className="pep-prod-section-link" href="#products-limited">
              {copy.portfolioAll} →
            </a>
          </div>

          {primary.length === 0 && !loading ? (
            <p className="text-secondary py-4">{copy.emptySearch}</p>
          ) : (
            <div className="pep-prod-portfolio__grid">
              {primary.map((editorial) => (
                <PrimaryCard
                  key={editorial.slug}
                  editorial={editorial}
                  locale={locale}
                  discoverLabel={copy.discover}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section
        className="pep-prod-limited-wrap"
        id="products-limited"
        data-section="P-LIMITED"
        aria-labelledby="products-limited-h2"
      >
        <div className="container">
          <div className="pep-prod-limited-wrap__head">
            <div>
              <p className="pep-prod-eyebrow">{copy.limitedEyebrow}</p>
              <h2 id="products-limited-h2" className="pep-prod-section-title">
                {copy.limitedTitle}
              </h2>
              <p className="pep-prod-section-lead">{copy.limitedLead}</p>
            </div>
            <aside className="pep-prod-limited-note">
              <strong>{copy.limitedContactTitle}</strong>
              <p>{copy.limitedContactBody}</p>
              <Link to="/contact">{copy.limitedContactCta} →</Link>
            </aside>
          </div>

          {limited.length > 0 ? (
            <div className="pep-prod-limited-wrap__grid">
              {limited.map((editorial) => (
                <LimitedCard
                  key={editorial.slug}
                  editorial={editorial}
                  locale={locale}
                  badge={copy.limitedBadge}
                  discoverLabel={copy.discover}
                />
              ))}
            </div>
          ) : null}
        </div>
      </section>

      <section className="pep-prod-platform" data-section="P-PLATFORM" aria-labelledby="products-platform-h2">
        <div className="container pep-prod-platform__inner">
          <div className="pep-prod-platform__copy">
            <p className="pep-prod-eyebrow pep-prod-eyebrow--on-dark">{copy.platformEyebrow}</p>
            <h2 id="products-platform-h2" className="pep-prod-platform__title">
              {copy.platformTitle}
            </h2>
            <p className="pep-prod-platform__lead">{copy.platformLead}</p>
            <Link className="pep-prod-platform__cta" to={copy.platformHref}>
              {copy.platformCta} →
            </Link>
          </div>
          <div className="pep-prod-platform-diagram" aria-hidden="true">
            <div className="pep-prod-platform-diagram__core">{copy.platformCenter}</div>
            {copy.platformNodes.map((n, i) => (
              <span key={n} className={`pep-prod-platform-diagram__node pep-prod-platform-diagram__node--${i}`}>
                {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="pep-prod-final" data-section="P-ACTION" aria-labelledby="products-final-h2">
        <div className="container pep-prod-final__inner">
          <div className="pep-prod-final__copy">
            <span className="pep-prod-final__icon" aria-hidden="true">
              ✦
            </span>
            <h2 id="products-final-h2" className="pep-prod-final__title">
              {copy.finalTitle}
            </h2>
            <p className="pep-prod-final__lead">{copy.finalLead}</p>
          </div>
          <div className="pep-prod-final__actions">
            <Link className="btn pep-prod-btn-final-primary" to={copy.finalConfigureHref}>
              {copy.finalConfigure} →
            </Link>
            <Link className="btn pep-prod-btn-final-secondary" to="/contact?type=demo">
              {copy.finalDemo} →
            </Link>
            <Link className="btn pep-prod-btn-final-secondary" to="/contact">
              {copy.finalContact} →
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
